import { useToast } from './use-toast';
import logger from '@/lib/logger';

// Make this interface exported so it can be imported elsewhere
export interface ErrorOptions {
  module?: string;
  showToast?: boolean;
  toastOptions?: {
    title?: string;
    variant?: 'default' | 'destructive';
    duration?: number;
  };
}

/**
 * Custom hook for centralized error handling
 * Provides a consistent way to log errors and optionally show toast notifications
 */
export function useErrorHandler() {
  const { toast } = useToast();

  /**
   * Handle an error with logging and optional toast notification
   * @param error The error to handle
   * @param message Optional message to override the error's message
   * @param options Additional options for error handling
   */
  const handleError = (
    error: unknown,
    message?: string,
    options: ErrorOptions = {}
  ) => {
    const { 
      module = 'App', 
      showToast = true, 
      toastOptions = {} 
    } = options;
    
    // Extract error message
    const errorMessage = message || (
      error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'An unknown error occurred'
    );
    
    // Log the error
    logger.error(errorMessage, {
      module,
      data: error
    });
    
    // Show toast if requested
    if (showToast) {
      toast({
        title: toastOptions.title || 'Error',
        description: errorMessage,
        variant: toastOptions.variant || 'destructive',
        duration: toastOptions.duration,
      });
    }
    
    return errorMessage;
  };
  
  /**
   * Wrap an async function with error handling
   * @param fn Async function to wrap
   * @param errorMessage Optional message to use if the function throws
   * @param options Additional options for error handling
   * @returns A new function that catches and handles errors
   */
  const withErrorHandling = <T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    errorMessage?: string,
    options: ErrorOptions = {}
  ) => {
    return async (...args: T): Promise<R | undefined> => {
      try {
        return await fn(...args);
      } catch (error) {
        handleError(error, errorMessage, options);
        return undefined;
      }
    };
  };
  
  return {
    handleError,
    withErrorHandling,
  };
}

export default useErrorHandler; 